import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IUser } from "../../interfaces/IUser";
import { useDispatch } from "react-redux";
import {
  userUpdateError,
  userUpdateStarted,
  userUpdateSuccess,
  userUploadRequestStarted,
  userUploadRequestError,
  userUploadRequestSuccess,
} from "../../state/user/userActions";
import AccountService from "../../services/AccountService";
import { IServiceResponse } from "../../interfaces/IServiceReponse";
import axios from "axios";
import { AccountContext } from "../../constants/Contexts";
import clsx from "clsx";
import FormField from "../Forms/FormField";
import { Button } from "react-bootstrap";

// Define interfaces for better type checking
interface FileWithPath {
  path: Blob;
  name: string;
}

interface ErrorState {
  FirstName: boolean;
  LastName: boolean;
}

const DetailsComponent = () => {
  const dispatch = useDispatch();
  const { account: accountDetail, setAccount: updateAccount } =
    useContext(AccountContext);

  // Refs
  const inputFile = useRef<HTMLInputElement>(null);

  // State management
  const [account, setAccount] = useState<IUser>(accountDetail!);
  const [displayImg, setDisplayImg] = useState<string | undefined>();
  const [imgLocal, setImgLocal] = useState(false);
  const [newPicture, setNewPicture] = useState<FileWithPath | null>(null);
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const [changes, setChanges] = useState(false);
  const [formInvalid, setFormInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState>({
    FirstName: false,
    LastName: false,
  });

  // Handle form input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [event.target.name]: event.target.value });
  };

  // Handle file selection for profile picture
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file: FileWithPath = {
      path: event.target.files[0],
      name: event.target.files[0].name,
    };

    setNewPicture(file);
    setAccount({
      ...account,
      ProfilePictureUrl: URL.createObjectURL(event.target.files[0]),
    });
    setImgLocal(true);
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) return;

    if (!errorState.FirstName && !errorState.LastName) {
      setIsSubmitting(true);
      const updated: IUser = { ...account };

      try {
        let imageUploaded = false;
        if (newPicture && uploadUrl) {
          imageUploaded = await uploadProfilePicture();
          if (imageUploaded) {
            updated.ProfilePictureUrl = uploadUrl.split("?")[0];
          }
        }

        await updateUser(updated);
      } catch (error) {
        console.error("Error during form submission:", error);
        onUpdateAccountError();
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setFormInvalid(true);
    }
  };

  // Trigger file input click when browse button is clicked
  const onBrowseClick = () => {
    if (inputFile.current) {
      inputFile.current.click();
    }
  };

  // Remove the selected image
  const onRemoveImage = () => {
    if (inputFile.current) {
      inputFile.current.value = "";
      setNewPicture(null);
      setAccount({ ...account, ProfilePictureUrl: null });
    }
  };

  // Validate form inputs
  const validate = useCallback(() => {
    setErrorState({
      FirstName: !account.FirstName || account.FirstName.trim().length === 0,
      LastName: !account.LastName || account.LastName.trim().length === 0,
    });
  }, [account.FirstName, account.LastName]);

  // Show success notification
  const onUpdateAccountSuccess = () => {
    // Using alert for now, but consider using a toast or notification system
    alert("Account details updated.");
  };

  // Show error notification
  const onUpdateAccountError = () => {
    // Using alert for now, but consider using a toast or notification system
    alert("Could not update account details.");
  };

  // Update user details through API
  const updateUser = async (account: IUser): Promise<boolean> => {
    if (!account) return false;

    setFormInvalid(false);
    dispatch(userUpdateStarted());

    try {
      const accountService = new AccountService(account.Token);
      const response = await accountService.updateUser(account);
      const serviceResponse: IServiceResponse = response.data;

      if (serviceResponse.success) {
        dispatch(userUpdateSuccess(account));
        setChanges(false);
        updateAccount(account);
        onUpdateAccountSuccess();
        return true;
      } else {
        dispatch(userUpdateError("Error updating user details."));
        onUpdateAccountError();
        return false;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      dispatch(userUpdateError(`Error updating user details: ${errorMessage}`));
      onUpdateAccountError();
      return false;
    }
  };

  // Get token for image upload
  const getUploadToken = useCallback(async () => {
    if (!newPicture) return;

    dispatch(userUploadRequestStarted(newPicture.name));

    try {
      const accountService = new AccountService(account.Token);
      const response = await accountService.getUploadToken(newPicture.name);
      const serviceResponse: IServiceResponse = response.data;

      if (serviceResponse.success) {
        dispatch(userUploadRequestSuccess(serviceResponse.data));
        setUploadUrl(serviceResponse.data);
      } else {
        dispatch(userUploadRequestError("Error getting upload token."));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      dispatch(
        userUploadRequestError(`Error getting upload token: ${errorMessage}`)
      );
    }
  }, [newPicture, account.Token, dispatch]);

  // Upload profile picture to storage
  const uploadProfilePicture = async (): Promise<boolean> => {
    if (!newPicture || !uploadUrl) return false;

    try {
      // Read the file as array buffer
      const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = reject;
        reader.readAsArrayBuffer(newPicture.path);
      });

      // Upload to the provided URL
      await axios.put(uploadUrl, arrayBuffer, {
        headers: { "x-ms-blob-type": "BlockBlob" },
      });

      return true;
    } catch (error) {
      console.error("Failed to upload image:", error);
      return false;
    }
  };

  // Helper components for better organization
  const ProfilePictureUploader = () => (
    <div className="mx-auto profile-picture">
      <div>
        <div className="dropzone">
          <input
            type="file"
            id="pictureSelector"
            name="ProfilePicture"
            ref={inputFile}
            accept="image/png, image/jpeg, image/jpg, image/gif"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <div
            className={clsx("dropzone", {
              "dz-started": account.ProfilePictureUrl,
            })}
          >
            <div className="dz-default dz-message">
              <button
                className="dz-button"
                type="button"
                onClick={onBrowseClick}
              >
                Select image to upload
              </button>
            </div>
            <div className="dz-image">
              {account.ProfilePictureUrl && (
                <img alt="Profile" src={displayImg} />
              )}
            </div>
          </div>
        </div>
        {account.ProfilePictureUrl && (
          <span className="delete-btn profile-remove" onClick={onRemoveImage}>
            Remove
          </span>
        )}
        <input type="hidden" name="newMedia" className="js-new-media" />
      </div>
    </div>
  );

  // Track changes between account state and the original data
  useEffect(() => {
    setChanges(JSON.stringify(account) !== JSON.stringify(accountDetail));
    validate();
  }, [account, accountDetail, validate]);

  // Update display image when profile picture URL changes
  useEffect(() => {
    if (!account.ProfilePictureUrl) return;

    let path = account.ProfilePictureUrl;
    if (!imgLocal) {
      // Add cache-busting timestamp for remote images
      path = `${path}?${new Date().getTime()}`;
    }
    setDisplayImg(path);
  }, [account.ProfilePictureUrl, imgLocal]);

  // Get upload token when a new picture is selected
  useEffect(() => {
    if (newPicture) {
      getUploadToken();
    }
  }, [getUploadToken, newPicture]);

  return (
    <div className="flex flex-col gap-6 p-4 md:p-10">
      <h2 className="text-heading-2">Details</h2>
      <form encType="multipart/form-data" method="post" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-12 max-w-[680px] w-full gap-6 md:gap-24">
          {/* Profile picture for mobile - appears at the top on small screens */}
          <div className="md:hidden col-span-full flex justify-center mb-12">
            <ProfilePictureUploader />
          </div>

          {/* Form fields */}
          <div className="col-span-full md:col-span-9 grid-cols-subgrid">
            <div className="col-span-full">
              <input
                id="Id"
                name="Id"
                type="hidden"
                value="80ba870a-21ef-40a4-972c-035c1353b93f"
              />
              <input id="photo-id" name="PhotoId" type="hidden" value="0" />
            </div>

            <div className="flex flex-col gap-6">
              <FormField
                label="First name"
                name="FirstName"
                placeholder="Enter your first name"
                value={account.FirstName}
                onChange={handleChange}
                error={errorState.FirstName}
                validated={formInvalid}
                errorMessage="First name is required"
                variant="horizontal"
              />

              <FormField
                label="Last name"
                name="LastName"
                placeholder="Enter your last name"
                value={account.LastName}
                onChange={handleChange}
                error={errorState.LastName}
                validated={formInvalid}
                errorMessage="Last name is required"
                variant="horizontal"
              />

              <FormField
                label="E-mail"
                name="Email"
                placeholder="Email@example.com"
                value={account.Email}
                disabled={true}
                variant="horizontal"
              />

              <FormField
                label="Organisation"
                name="Organisation"
                placeholder="Organisation"
                value={account.OrganisationName || ""}
                disabled={true}
                variant="horizontal"
              />
            </div>
          </div>

          {/* Profile picture for desktop - appears at the right on larger screens */}
          <div className="hidden md:block md:col-span-3">
            <ProfilePictureUploader />
          </div>

          <div className="col-span-full flex flex-col md:flex-row justify-between items-center mt-4">
            <div>
              <span
                className={clsx("unsaved-message", {
                  "unsaved-show": changes,
                })}
              >
                {changes && (
                  <span className="alert alert-dismissable alert-info">
                    You have unsaved changes.
                  </span>
                )}
              </span>
            </div>

            <div className="mt-4 md:mt-0">
              <div className="submit-btn">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  className={clsx("_submit", {
                    "opacity-50 cursor-not-allowed": isSubmitting,
                  })}
                  aria-label={isSubmitting ? "Saving..." : "Save"}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DetailsComponent;
